import { z } from "zod";
import { narrativeBookProductSchema } from "./narrative-book-production.js";
import { narrativePageArchetypeSchema } from "./narrative-visual-production.js";
export const narrativeReadingOrderElementSchema = z
    .object({
    order: z.number().int().nonnegative(),
    role: z.enum(["TITLE", "HEADING", "PARAGRAPH", "FIGURE", "CAPTION", "PAGE_NUMBER"]),
    label: z.string().trim().min(1).max(1_000),
    artifact: z.boolean(),
})
    .strict();
export const narrativeAccessibilityEvidenceSchema = z
    .object({
    contract: z.literal("narrative-accessibility-evidence/1"),
    product: narrativeBookProductSchema,
    documentLanguage: z.literal("en-US"),
    searchableText: z.literal(true),
    bookmarks: z.literal(true),
    taggedPdf: z.literal(false),
    pdfUaConformanceClaimed: z.literal(false),
    verifiedAssistiveTechnologyReadingOrder: z.literal(false),
    status: z.literal("BLOCKED_PENDING_TAGGED_PDF_REMEDIATION"),
    pages: z.array(z
        .object({
        pdfPageIndex: z.number().int().nonnegative(),
        archetype: narrativePageArchetypeSchema,
        intentionalBlank: z.boolean(),
        elements: z.array(narrativeReadingOrderElementSchema),
    })
        .strict()),
    artifactDescriptions: z.array(z
        .object({
        assetId: z.string().trim().min(1).max(200),
        alternateDescription: z.string().trim().min(1).max(1_000),
        caption: z.string().trim().min(1).max(1_000),
    })
        .strict()),
    remediation: z
        .tuple([
        z.literal("CREATE_STRUCTURE_TREE_AND_MARKED_CONTENT"),
        z.literal("BIND_ARTIFACT_DESCRIPTIONS_TO_FIGURE_TAGS"),
        z.literal("VERIFY_PAIRED_COLUMNS_IN_LOGICAL_ORDER"),
        z.literal("TEST_WITH_SCREEN_READER_AND_KEYBOARD"),
        z.literal("VALIDATE_PDF_UA_WITH_INDEPENDENT_TOOL"),
    ])
        .readonly(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.pages.some((page, index) => page.pdfPageIndex !== index))
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Reading-order pages must be complete and sequential.",
        });
    for (const [pageIndex, page] of value.pages.entries()) {
        if (page.intentionalBlank !== (page.archetype === "INTENTIONAL_BLANK"))
            context.addIssue({
                code: "custom",
                path: ["pages", pageIndex, "intentionalBlank"],
                message: "Only intentional-blank archetypes may have blank status.",
            });
        if (page.intentionalBlank && page.elements.length > 0)
            context.addIssue({
                code: "custom",
                path: ["pages", pageIndex, "elements"],
                message: "Intentional blank pages must not enter reading order.",
            });
        if (!page.intentionalBlank && page.elements.length === 0)
            context.addIssue({
                code: "custom",
                path: ["pages", pageIndex, "elements"],
                message: "Every nonblank page needs a planned logical reading order.",
            });
        if (page.elements.some((element, index) => element.order !== index))
            context.addIssue({
                code: "custom",
                path: ["pages", pageIndex, "elements"],
                message: "Reading-order elements must be sequential within each page.",
            });
    }
});
export const narrativeAccessibleDigitalEditionSchema = z
    .object({
    contract: z.literal("narrative-accessible-digital-edition/1"),
    product: narrativeBookProductSchema,
    filename: z.string().trim().min(1).max(255),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    documentLanguage: z.literal("en-US"),
    reflowable: z.literal(true),
    semanticLandmarks: z.array(z.enum(["HEADER", "NAV", "MAIN", "ARTICLE", "FOOTER"])).min(4),
    headingCount: z.number().int().positive(),
    paragraphCount: z.number().int().positive(),
    figureDescriptionCount: z.number().int().nonnegative(),
    sourceParagraphIds: z.array(z.string().regex(/^[A-Z0-9]+:p\d+$/u)),
    renderedParagraphIds: z.array(z.string().regex(/^[A-Z0-9]+:p\d+$/u)),
    keyboardSkipLink: z.literal(true),
    colorIndependentAttribution: z.literal(true),
    pdfCompanionStatus: z.literal("BLOCKED_PENDING_TAGGED_PDF_REMEDIATION"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.sourceParagraphIds.join("|") !== value.renderedParagraphIds.join("|"))
        context.addIssue({
            code: "custom",
            path: ["renderedParagraphIds"],
            message: "The reflowable edition must preserve every source paragraph exactly once and in order.",
        });
});
//# sourceMappingURL=narrative-accessibility.js.map